import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import ContactForm from "@/components/ContactForm";
import { Mail, Linkedin, MessageSquare, ArrowRight } from "lucide-react";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="page-hero text-white py-16 md:py-20">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl">
              <h1 className="heading-display text-white mb-5 font-['Manrope']">
                Contact Us
              </h1>
              <p className="text-lg md:text-xl text-[#94A3B8] leading-relaxed">
                Have a question about workshops or training? Get in touch and we'll 
                be happy to help.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-white section-spacing" data-testid="contact-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Contact Info */}
            <AnimatedSection>
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Get in touch
              </h2>
              <p className="text-body-lg mb-8">
                Whether you're interested in a workshop for yourself, your team, or your 
                entire organisation, we'd love to hear from you. Fill out the form or 
                reach out directly.
              </p>

              {/* Contact Details */}
              <div className="space-y-5 mb-10">
                <div className="flex items-start gap-4">
                  <div className="icon-wrapper flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A192F] font-['Manrope'] mb-1">Email</h3>
                    <a 
                      href="mailto:info@beaiready.com.au" 
                      className="text-[#008A90] hover:underline text-body"
                      data-testid="contact-email-link"
                    >
                      info@beaiready.com.au
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="icon-wrapper flex-shrink-0">
                    <Linkedin size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A192F] font-['Manrope'] mb-1">LinkedIn</h3>
                    <a 
                      href="https://linkedin.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#008A90] hover:underline text-body"
                      data-testid="contact-linkedin-link"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              {/* Discovery Call */}
              <div className="p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <div className="flex items-start gap-4">
                  <div className="icon-wrapper flex-shrink-0 bg-[#008A90] text-white border-0">
                    <MessageSquare size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A192F] mb-2 font-['Manrope']">
                      Book a Discovery Call
                    </h3>
                    <p className="text-body-sm mb-3">
                      Want to discuss your specific needs? Book a quick call to explore 
                      how we can help you or your team become AI ready.
                    </p>
                    <p className="text-sm text-[#64748B] italic">
                      Fill out the form to request a discovery call.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Contact Form */}
            <AnimatedSection delay={0.15}>
              <div className="bg-[#F8FAFC] p-6 md:p-8 lg:p-10 rounded-2xl border border-[#E2E8F0]">
                <h3 className="text-xl md:text-2xl font-bold text-[#0A192F] mb-6 font-['Manrope']">
                  Send us a message
                </h3>
                <ContactForm />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="bg-[#F8FAFC] section-spacing" data-testid="faq-preview-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="heading-section text-[#0A192F] mb-8 font-['Manrope'] text-center">
                Common Questions
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl border border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0A192F] mb-2 font-['Manrope']">
                    Do I need technical experience to attend?
                  </h3>
                  <p className="text-body">
                    No. Our workshops are designed for professionals at all levels, 
                    regardless of technical background. We explain everything clearly 
                    and focus on practical application.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0A192F] mb-2 font-['Manrope']">
                    Can workshops be customised for our team?
                  </h3>
                  <p className="text-body">
                    Yes. We offer customised workshops tailored to your team's specific 
                    needs, industry, and objectives. Get in touch to discuss your requirements.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0A192F] mb-2 font-['Manrope']">
                    Are workshops available online?
                  </h3>
                  <p className="text-body">
                    Yes. We offer both in-person and virtual workshop options to suit 
                    your team's needs and location.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white section-spacing" data-testid="contact-final-cta">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Not sure where to start?
              </h2>
              <p className="text-body-lg mb-8">
                Browse our workshop options to find the right fit for your needs.
              </p>
              <Link to="/workshops">
                <Button 
                  className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                  data-testid="contact-view-workshops-btn"
                >
                  View Our Workshops
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
